import React, { useEffect, useId, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip } from '@fortawesome/pro-solid-svg-icons/faPaperclip';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import type { KnowledgeAttachment } from 'shared/types/knowledge';
import { Modal } from 'src/components/design-system';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { AttachmentViewer } from './attachment-viewer.component';
import styles from './attachments-section.module.scss';

type AttachmentsSectionProps = {
  attachments?: KnowledgeAttachment[];
  onOpen?: (attachment: KnowledgeAttachment) => void;
  loading?: boolean;
};

export function AttachmentsSection({
  attachments = [],
  loading = false,
  onOpen,
}: AttachmentsSectionProps) {
  const lang = useSelector(getLang('KNOWLEDGE'));
  const headingId = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const attachmentParam = searchParams.get('attachment');
  const openAttachment =
    attachments.find(attachment => attachment._id === attachmentParam) ?? null;
  const notifiedIdRef = useRef<string | null>(null);

  // Covers deep links too, and fires once per open — a row click also lands here via the param
  useEffect(() => {
    if (!openAttachment) {
      notifiedIdRef.current = null;
      return;
    }
    if (notifiedIdRef.current !== openAttachment._id) {
      notifiedIdRef.current = openAttachment._id;
      onOpen?.(openAttachment);
    }
  }, [openAttachment, onOpen]);

  // Strip an unknown id only after the full item loaded, or a valid deep link would be dropped
  useEffect(() => {
    if (attachmentParam && !loading && !openAttachment) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('attachment');
      setSearchParams(nextParams, { replace: true });
    }
  }, [attachmentParam, loading, openAttachment, searchParams, setSearchParams]);

  if (!attachments.length) {
    return null;
  }

  const openInModal = (attachment: KnowledgeAttachment) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('attachment', attachment._id);
    setSearchParams(nextParams, { state: { attachmentPushed: true } });
  };

  const closeModal = () => {
    // Pop the entry a row click pushed; replace on a deep link, which pushed nothing
    if (location.state?.attachmentPushed) {
      navigate(-1);
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('attachment');
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.heading}>
        {lang.ATTACHMENTS_COUNT?.replace('{count}', formatNumber(attachments.length))}
      </h2>
      <ListItemsNavigator orientation="vertical" className={styles.list}>
        {attachments.map(attachment => (
          <button
            key={attachment._id}
            type="button"
            className={styles.row}
            aria-label={lang.OPEN_ATTACHMENT?.replace('{title}', attachment.title)}
            onClick={() => openInModal(attachment)}>
            <FontAwesomeIcon icon={faPaperclip} className={styles.icon} />
            <span className={styles.title}>{attachment.title}</span>
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={styles.openIcon} />
          </button>
        ))}
      </ListItemsNavigator>
      <Modal
        open={!!openAttachment}
        onCancel={closeModal}
        title={openAttachment?.title}
        footer={null}
        width="90%"
        className={styles.viewerModal}>
        {openAttachment && (
          <AttachmentViewer key={openAttachment._id} attachment={openAttachment} />
        )}
      </Modal>
    </section>
  );
}
